import React, { useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Loader2 } from 'lucide-react';
import PageMeta from '../components/common/PageMeta';
import Alert from '../components/ui/alert/Alert';
import { appConfigService } from '../services/AppConfigService';
import { downloadCSV } from '../utils/csvConverter';
import { settingsService } from '../services/SettingsService';
import { filesService } from '../services/FilesService';
import { useNavigate } from 'react-router-dom';
import { getGemValue } from '../utils/getGemValue';
import { textToJsonParserService } from '../services/TextToJsonParserService';
import { processingService } from '../services/ProcessingService';
import PageBreadcrumb from '../components/common/PageBreadCrumb';
import AppStarted from '../components/AppStarted';
import { clientPdfDocumentService } from '../services/ClientPdfDocumentService';
import { userTokensService } from '../services/UserTokensService';

// Add sanitization function to prevent XSS
const sanitizeFileName = (fileName: string): string => {
  // Basic sanitization to remove potentially dangerous characters
  // Remove HTML tags, control chars, and limit to safe characters
  const sanitized = fileName
    .replace(/[<>:"'/\\|?*\x00-\x1F]/g, '_') // Replace dangerous characters with underscores
    .trim();
  
  // If the sanitization removed all characters, provide a default name
  return sanitized || 'unnamed_file.pdf';
};

export default function Upload() {
  const settings = settingsService.getSettings();
  const downloadResultImmediately = settings.downloadResultImmediately;
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [apiResponse, setApiResponse] = useState<any[]>([]);
  const [alert, setAlert] = useState<{
    show: boolean;
    variant: "success" | "error" | "warning" | "info";
    title: string;
    message: string;
    duration: number;
    showProgress: boolean;
  } | null>(null);
  const navigate = useNavigate();

  const setAutoClosingAlert = (alertData: {
    show: boolean;
    variant: "success" | "error" | "warning" | "info";
    title: string;
    message: string;
  }) => {
    setAlert({
      ...alertData,
      duration: 30000, // 30 seconds
      showProgress: true
    });
    const timeoutId = setTimeout(() => {
      setAlert(null);
    }, 30000);

    return () => clearTimeout(timeoutId);
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setApiResponse([]);
    setAlert(null);

    try {
      // Sanitize the file name
      const sanitizedFileName = sanitizeFileName(file.name);
      
      // Create a new File object with the sanitized name if it's different
      let fileToProcess = file;
      if (sanitizedFileName !== file.name) {
        fileToProcess = new File([file], sanitizedFileName, { type: file.type });
      }

      // Get page count before processing
      const pageCount = await clientPdfDocumentService.getPageCountFromWebFile(fileToProcess);
      console.log(`From local: PDF has ${pageCount} pages`);

      // check if user has enough tokens
      const tokenInfo = await userTokensService.checkTokenAllowance(pageCount);
      console.log('tokenInfo is', tokenInfo);
      if (tokenInfo && tokenInfo.allowed === false) {
        setAutoClosingAlert({
          show: true,
          variant: "error",
          title: "Too many pages in the file",
          //message: "This account does not have enough sufficient allowance left to process this file.",
          message: tokenInfo.reason || "This account does not have enough sufficient allowance left to process this file."
        });
        return;
      }

      // Use the ProcessingService to process the file
      const result = await processingService.processPdf(fileToProcess);

      if (result.success && result.parsedData) {
        // Set the API response for display
        setApiResponse(result.parsedData);

        if (downloadResultImmediately) {
          // Automatically download CSV when data is successfully parsed
          downloadCSV(result.parsedData, sanitizedFileName); // Use sanitized filename
        }

        const updatedTokenInfo = await userTokensService.useTokens(pageCount);
        console.log('updatedTokenInfo is', updatedTokenInfo);

        // Navigate to files table
        navigate('/files');

        setAutoClosingAlert({
          show: true,
          variant: "success",
          title: "File Processed",
          message: "Your file has been successfully uploaded and processed"
        });
      } else {
        setAutoClosingAlert({
          show: true,
          variant: "error",
          title: "Processing Failed",
          message: result.message || "There was an error processing your file."
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setAutoClosingAlert({
        show: true,
        variant: "error",
        title: "Upload Failed",
        message: "There was an error processing your file. Please try again."
      });
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      if (!selectedFile) {
        setAutoClosingAlert({
          show: true,
          variant: "error",
          title: "No File Selected",
          message: "Please select a valid PDF file"
        });
        return;
      }
      await handleFileUpload(selectedFile);
    },
    onDropRejected: (fileRejections: FileRejection[]) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        if (rejection.file.size > 20 * 1024 * 1024) { // 20MB limit
          setAutoClosingAlert({
            show: true,
            variant: "error",
            title: "File Too Large",
            message: "File size exceeds 20MB limit"
          });
          return;
        }
        if (rejection.file.type !== 'application/pdf') {
          setAutoClosingAlert({
            show: true,
            variant: "error",
            title: "Invalid File Type",
            message: "Only PDF files are allowed"
          });
          return;
        }
      }
    },
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    multiple: false
  });

  return (
    <>
      <AppStarted />
      <PageMeta
        title="Drop and Process - PDF ConvertOne"
        description="Drop and Process your PDF file for conversion"
      />
      <PageBreadcrumb pageTitle="Drop and Process" />

      <div className="w-full p-2 md:p-6 lg:p-8">
        {alert && alert.show && (
          <div className="mb-6">
            <Alert
              variant={alert.variant}
              title={alert.title}
              message={alert.message}
              showLink={false}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        <div className="mx-auto max-w-2xl">
          <h1 className="mb-6 text-2xl font-semibold text-gray-800 dark:text-white/90">
            Drop a file to start conversion
          </h1>

          <div className="transition border border-gray-300 border-dashed cursor-pointer dark:hover:border-brand-500 dark:border-gray-700 rounded-xl hover:border-brand-500">
            <div
              {...getRootProps()}
              className={`p-8 text-center ${isDragActive
                ? "border-brand-500 bg-gray-100 dark:bg-gray-800"
                : "border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
                }`}
            >
              <input {...getInputProps()} />

              <div className="flex flex-col items-center">
                {isUploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Processing your file...
                    </p>
                  </div>
                ) : (
                  <>
                    <svg
                      className="w-16 h-16 mb-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="mb-2 text-xl font-semibold text-gray-700 dark:text-gray-300">
                      {isDragActive ? "Drop the file here" : "Drag & Drop PDF file here"}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      or click to select file (max 20MB)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {file && !isUploading && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Selected File:
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {sanitizeFileName(file.name)} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}